import React from 'react';
import styles from './BenefitsSection.module.css';

const CARD_POSITIONS = [
  styles.cardConvenience,
  styles.cardCost,
  styles.cardExperience,
  styles.cardDecision,
];

const BORDER_CLASSES = [
  styles.borderGreen,
  styles.borderOrange,
  styles.borderTeal,
  styles.borderAmber,
];

const PIN_COLORS = ['#1b8b3d', '#ff671b', '#0d9488', '#d97706'];

const BenefitsSection = ({
  benefits = null,
  title = 'Product Benefits',
  subtitle = 'Key advantages that make our food wrap paper preferred choice for quality-conscious brands worldwide',
}) => {
  const defaultBenefits = [
    {
      title: 'Convenience Gain',
      description:
        'Makes food wrapping simple and efficient for both hot and cold packaging. It saves time in preparation and serving, improving overall workflow for businesses.',
    },
    {
      title: 'Cost Saving',
      description:
        'Helps reduce packaging expenses with minimal waste and efficient material usage. Ideal for bulk operations, ensuring better cost control without losing quality.',
    },
    {
      title: 'Customer Experience',
      description:
        'Delivers a clean, hygienic, and non-sticky food presentation that enhances customer satisfaction and keeps food fresh, warm, and neatly packed.',
    },
    {
      title: 'Safety & Quality',
      description:
        'Made from food-grade certified, eco-friendly, and compostable materials that ensure safe usage and meet modern sustainability standards.',
    },
  ];

  const benefitsToShow = benefits || defaultBenefits;

  const titleParts = title.includes('Benefits')
    ? { lead: title.replace(/\s*Benefits\s*$/i, '').trim() || 'Product', accent: 'Benefits' }
    : { lead: title, accent: null };

  return (
    <section className={styles.benefitsContainer} aria-labelledby="product-benefits-heading">
      <div className={styles.bgDecor} aria-hidden="true">
        <span className={styles.watermark}>GREENERI</span>
        <div className={styles.orbGreen} />
        <div className={styles.orbOrange} />
      </div>

      <div className={styles.wrapper}>
        <div className={styles.sectionHeadingBlock}>
          <h2 id="product-benefits-heading" className={styles.headTitle}>
            {titleParts.accent ? (
              <>
                {titleParts.lead}{' '}
                <span className={styles.titleAccent}>{titleParts.accent}</span>
              </>
            ) : (
              titleParts.lead
            )}
          </h2>
          {subtitle && <p className={styles.headSubtitle}>{subtitle}</p>}
        </div>

        <div className={styles.diagramArea}>
          {benefitsToShow.map((benefit, index) => (
            <div
              key={`${benefit.title}-${index}`}
              className={`${styles.infoCard} ${CARD_POSITIONS[index] || styles.cardDecision}`}
            >
              <div className={`${styles.cardBody} ${BORDER_CLASSES[index] || styles.borderAmber}`}>
                <span className={styles.cardIndex}>{String(index + 1).padStart(2, '0')}</span>
                <h3 className={styles.cardTitle}>{benefit.title}</h3>
                <p className={styles.cardDesc}>{benefit.description}</p>
              </div>
              <div className={styles.pinPointer} aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M12 21L4 12H20L12 21Z" fill={PIN_COLORS[index] || PIN_COLORS[3]} />
                </svg>
                <div className={styles.pinShadow} />
              </div>
            </div>
          ))}

          <div className={styles.dialFrame}>
            <svg
              className={styles.arcSvg}
              viewBox="0 0 400 200"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M 200 200 L 20 200 A 180 180 0 0 1 72.6 72.6 Z" fill="#1b8b3d" />
              <path d="M 200 200 L 72.6 72.6 A 180 180 0 0 1 200 20 Z" fill="#ff671b" />
              <path d="M 200 200 L 200 20 A 180 180 0 0 1 327.4 72.6 Z" fill="#0d9488" />
              <path d="M 200 200 L 327.4 72.6 A 180 180 0 0 1 380 200 Z" fill="#d97706" />
              <circle cx="200" cy="200" r="75" fill="#f7faf8" />
            </svg>

            <div className={`${styles.arcIcon} ${styles.iconConvenience}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
                <path d="M9 16l2 2 4-4" />
              </svg>
            </div>

            <div className={`${styles.arcIcon} ${styles.iconCost}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8V16" />
                <path d="M14.5 10.5H11a1.5 1.5 0 0 0 0 3h2a1.5 1.5 0 0 1 0 3H9.5" />
              </svg>
            </div>

            <div className={`${styles.arcIcon} ${styles.iconExperience}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>

            <div className={`${styles.arcIcon} ${styles.iconDecision}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
                <path d="M19 5L14 10" />
                <path d="M21 3h-6V9" />
              </svg>
            </div>

            <div className={styles.brainMaskBase} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
